import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/Card';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import { Modal } from '../ui/Modal';
import { TrashIcon, PencilIcon, CheckIcon, XMarkIcon } from '@heroicons/react/24/outline';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, ReferenceLine, PieChart, Pie, Cell, Legend,
} from 'recharts';
import toast from 'react-hot-toast';
import apiClient from '../../utils/api';

import { RangeSelector, useTimeRange } from './TimeRange';

const BEVERAGE_COLORS = {
  water: '#5FA8C7',
  coffee: '#92400e',
  tea: '#16a34a',
  juice: '#f97316',
  milk: '#e5e7eb',
  soda: '#C77D6A',
  other: '#6b7280',
};

const normalizeBeverage = (entry) => {
  const type = (entry.data?.beverage_type || entry.entry_type || 'water').toLowerCase();
  if (type.includes('water')) return 'water';
  if (type.includes('coffee')) return 'coffee';
  if (type.includes('tea')) return 'tea';
  if (type.includes('juice')) return 'juice';
  if (type.includes('milk')) return 'milk';
  if (type.includes('soda') || type.includes('pop')) return 'soda';
  return 'other';
};

const getAmountMl = (entry) => {
  return (entry.data?.amount_ml) || entry.value || 0;
};

const HydrationDataView = () => {
  const [allEntries, setAllEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const { inRange, spanDays, selectorProps } = useTimeRange('1M');
  const [hydrationTarget, setHydrationTarget] = useState(null);
  const [editingTarget, setEditingTarget] = useState(false);
  const [targetInput, setTargetInput] = useState('');

  // Log table filters
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  // Delete modal
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deletingEntryId, setDeletingEntryId] = useState(null);

  // Edit modal
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingEntry, setEditingEntry] = useState(null);
  const [editForm, setEditForm] = useState({});

  const fetchEntries = useCallback(async () => {
    setLoading(true);
    try {
      const response = await apiClient.get('/api/health/entries', {
        params: { category: 'nutrition.hydration', limit: 1000 },
      });
      setAllEntries(response.data);
    } catch (error) {
      console.error('Error fetching hydration entries:', error);
      toast.error('Failed to fetch hydration entries');
    } finally {
      setLoading(false);
    }
  }, []);

  // Hydration target lives in the unified MetricTarget store (shared with the
  // You page + coaching). Simple single-number UX preserved: it maps to the
  // band's floor (ultimate_low, "aim for at least"); only a user-set (custom)
  // value shows as the target.
  const fetchTarget = useCallback(async () => {
    try {
      const res = await apiClient.get('/api/twin/targets');
      const h = (res.data || []).find((t) => t.metric_key === 'hydration_ml');
      setHydrationTarget(
        h && h.ultimate_is_custom && h.ultimate_low != null ? h.ultimate_low : null
      );
    } catch {
      // Targets are optional context — never block the hydration view.
    }
  }, []);

  useEffect(() => {
    fetchEntries();
    fetchTarget();
  }, [fetchEntries, fetchTarget]);

  // Filter entries for charts by time range
  const chartEntries = useMemo(() => {
    if (!allEntries.length) return [];
    return allEntries.filter(e => inRange(e.entry_date));
  }, [allEntries, inRange]);

  // Filter entries for the log table
  const logEntries = useMemo(() => {
    return allEntries.filter(e => {
      if (startDate && e.entry_date < startDate) return false;
      if (endDate && e.entry_date > endDate) return false;
      return true;
    });
  }, [allEntries, startDate, endDate]);

  // Aggregate data by date for bar chart
  const dailyData = useMemo(() => {
    const byDate = {};
    chartEntries.forEach(entry => {
      const date = entry.entry_date;
      if (!byDate[date]) {
        byDate[date] = { date, water: 0, coffee: 0, tea: 0, juice: 0, milk: 0, soda: 0, other: 0, total: 0 };
      }
      const bev = normalizeBeverage(entry);
      const ml = getAmountMl(entry);
      byDate[date][bev] += ml;
      byDate[date].total += ml;
    });
    return Object.values(byDate).sort((a, b) => a.date.localeCompare(b.date));
  }, [chartEntries]);

  // Summary stats
  const stats = useMemo(() => {
    if (!dailyData.length) return null;
    const days = dailyData.length;
    const totalMl = dailyData.reduce((s, d) => s + d.total, 0);
    const daysAtTarget = hydrationTarget
      ? dailyData.filter(d => d.total >= hydrationTarget).length
      : null;

    // Most common beverage
    const bevTotals = {};
    chartEntries.forEach(entry => {
      const bev = normalizeBeverage(entry);
      bevTotals[bev] = (bevTotals[bev] || 0) + getAmountMl(entry);
    });
    const topBeverage = Object.entries(bevTotals).sort((a, b) => b[1] - a[1])[0]?.[0] || '-';

    return {
      avgDaily: Math.round(totalMl / days),
      daysAtTarget,
      totalLiters: (totalMl / 1000).toFixed(1),
      topBeverage: topBeverage.charAt(0).toUpperCase() + topBeverage.slice(1),
      days,
    };
  }, [dailyData, chartEntries, hydrationTarget]);

  // Donut chart — beverage breakdown
  const donutData = useMemo(() => {
    const bevTotals = {};
    chartEntries.forEach(entry => {
      const bev = normalizeBeverage(entry);
      bevTotals[bev] = (bevTotals[bev] || 0) + getAmountMl(entry);
    });
    const total = Object.values(bevTotals).reduce((s, v) => s + v, 0);
    if (!total) return [];
    return Object.entries(bevTotals)
      .filter(([, v]) => v > 0)
      .map(([name, value]) => ({
        name: name.charAt(0).toUpperCase() + name.slice(1),
        value,
        pct: Math.round((value / total) * 100),
        color: BEVERAGE_COLORS[name] || BEVERAGE_COLORS.other,
      }))
      .sort((a, b) => b.value - a.value);
  }, [chartEntries]);

  // Handlers
  const handleDeleteClick = (entryId) => {
    setDeletingEntryId(entryId);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!deletingEntryId) return;
    try {
      await apiClient.delete(`/api/health/entries/${deletingEntryId}`);
      toast.success('Entry deleted');
      setDeleteDialogOpen(false);
      setDeletingEntryId(null);
      fetchEntries();
    } catch (error) {
      console.error('Error deleting entry:', error);
      toast.error('Failed to delete entry');
    }
  };

  const handleEditClick = (entry) => {
    const data = entry.data || {};
    setEditingEntry(entry);
    setEditForm({
      beverage_type: data.beverage_type || entry.entry_type || 'water',
      amount_ml: data.amount_ml || entry.value || '',
      entry_date: entry.entry_date || '',
      notes: entry.notes || '',
    });
    setEditDialogOpen(true);
  };

  const handleEditSave = async () => {
    if (!editingEntry) return;
    try {
      const updatedData = {
        ...editingEntry.data,
        beverage_type: editForm.beverage_type,
        amount_ml: editForm.amount_ml !== '' ? parseFloat(editForm.amount_ml) : null,
      };
      await apiClient.put(`/api/health/entries/${editingEntry.id}`, {
        entry_date: editForm.entry_date,
        notes: editForm.notes || null,
        data: updatedData,
      });
      toast.success('Entry updated');
      setEditDialogOpen(false);
      setEditingEntry(null);
      fetchEntries();
    } catch (error) {
      console.error('Error updating entry:', error);
      toast.error('Failed to update entry');
    }
  };

  const saveTarget = async () => {
    const val = targetInput ? parseFloat(targetInput) : null;
    try {
      if (val == null) {
        await apiClient.post('/api/twin/targets/hydration_ml/reset');
      } else {
        // Single number = the floor ("at least"); open-ended above.
        await apiClient.patch('/api/twin/targets/hydration_ml', {
          ultimate_low: val,
          ultimate_high: null,
        });
      }
      setHydrationTarget(val);
      setEditingTarget(false);
      toast.success('Hydration target updated');
    } catch (error) {
      toast.error('Failed to update hydration target');
    }
  };

  const startEditTarget = () => {
    setTargetInput(hydrationTarget != null ? String(hydrationTarget) : '');
    setEditingTarget(true);
  };

  // Chart formatting
  const formatDate = (dateStr) => {
    const d = new Date(dateStr + 'T00:00:00');
    const days = spanDays;
    if (days && days <= 90) {
      return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    }
    return d.toLocaleDateString('en-US', { month: 'short', year: '2-digit' });
  };

  const tickInterval = Math.max(0, Math.ceil(dailyData.length / 8) - 1);

  // Tooltips
  const BarTooltip = ({ active, payload }) => {
    if (!active || !payload?.length) return null;
    const d = payload[0]?.payload;
    if (!d) return null;
    const dateLabel = new Date(d.date + 'T00:00:00').toLocaleDateString('en-US', {
      weekday: 'short', month: 'short', day: 'numeric',
    });
    const beverages = ['water', 'coffee', 'tea', 'juice', 'milk', 'soda', 'other'];
    return (
      <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-3 shadow-lg text-sm">
        <p className="font-semibold text-gray-900 dark:text-white mb-2">{dateLabel}</p>
        <div className="space-y-1">
          {beverages.map(bev => d[bev] > 0 && (
            <p key={bev} style={{ color: BEVERAGE_COLORS[bev] }}>
              {bev.charAt(0).toUpperCase() + bev.slice(1)}: <span className="font-semibold">{Math.round(d[bev])} ml</span>
            </p>
          ))}
          <p className="border-t border-gray-200 dark:border-gray-600 pt-1 mt-1 font-semibold text-gray-900 dark:text-white">
            Total: {Math.round(d.total)} ml
          </p>
        </div>
      </div>
    );
  };

  const DonutTooltip = ({ active, payload }) => {
    if (!active || !payload?.length) return null;
    const d = payload[0]?.payload;
    return (
      <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-3 shadow-lg text-sm">
        <p style={{ color: d.color }} className="font-semibold">{d.name}: {d.pct}%</p>
        <p className="text-gray-600 dark:text-gray-300">{Math.round(d.value)} ml total</p>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (!allEntries.length) {
    return (
      <Card>
        <CardContent className="py-12">
          <p className="text-center text-gray-600 dark:text-gray-300">
            No hydration entries found. Use the AI input to log drinks (e.g. "drank 500ml water").
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Range Selector */}
      <RangeSelector
        {...selectorProps}
        right={(
          <span className="shrink-0 text-sm text-gray-600 dark:text-gray-300">
            {stats?.days || 0} days with data
          </span>
        )}
      />

      {/* Summary Stat Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4">
            <p className="text-sm text-gray-600 dark:text-gray-300">Avg Daily Intake</p>
            <p className="text-2xl font-bold text-gray-900 dark:text-white">
              {stats?.avgDaily ?? '--'}
            </p>
            {hydrationTarget && stats?.avgDaily ? (
              <p className={`text-sm mt-1 ${stats.avgDaily >= hydrationTarget ? 'text-green-500' : 'text-red-400'}`}>
                {stats.avgDaily >= hydrationTarget ? 'Meeting' : 'Below'} target ({hydrationTarget} ml)
              </p>
            ) : (
              <p className="text-sm text-gray-600 dark:text-gray-300 mt-1">ml/day</p>
            )}
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <p className="text-sm text-gray-600 dark:text-gray-300">Days at Target</p>
            <p className="text-2xl font-bold text-gray-900 dark:text-white">
              {stats?.daysAtTarget != null ? stats.daysAtTarget : '--'}
            </p>
            <p className="text-sm text-gray-600 dark:text-gray-300 mt-1">
              {stats?.days ? `of ${stats.days} days` : 'Set a target'}
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <p className="text-sm text-gray-600 dark:text-gray-300">Total Volume</p>
            <p className="text-2xl font-bold text-gray-900 dark:text-white">
              {stats?.totalLiters ?? '--'}
            </p>
            <p className="text-sm text-gray-600 dark:text-gray-300 mt-1">liters</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <p className="text-sm text-gray-600 dark:text-gray-300">Top Beverage</p>
            <p className="text-2xl font-bold text-gray-900 dark:text-white">
              {stats?.topBeverage ?? '--'}
            </p>
            <p className="text-sm text-gray-600 dark:text-gray-300 mt-1">by volume</p>
          </CardContent>
        </Card>
      </div>

      {/* Hydration Target Inline Editor */}
      <div className="flex items-center gap-3">
        <span className="text-sm text-gray-600 dark:text-gray-300">Daily Hydration Target:</span>
        {editingTarget ? (
          <div className="flex items-center gap-2">
            <Input
              type="number"
              value={targetInput}
              onChange={(e) => setTargetInput(e.target.value)}
              placeholder="e.g. 2500"
              className="w-32"
              min="0"
              autoFocus
              onKeyDown={(e) => {
                if (e.key === 'Enter') saveTarget();
                if (e.key === 'Escape') setEditingTarget(false);
              }}
            />
            <button onClick={saveTarget} className="p-1 text-green-600 hover:text-green-700">
              <CheckIcon className="w-5 h-5" />
            </button>
            <button onClick={() => setEditingTarget(false)} className="p-1 text-gray-400 hover:text-gray-600">
              <XMarkIcon className="w-5 h-5" />
            </button>
          </div>
        ) : (
          <button
            onClick={startEditTarget}
            className="flex items-center gap-1.5 text-sm font-medium text-gray-700 dark:text-gray-300 hover:text-primary-600 dark:hover:text-primary-400 transition-colors"
          >
            {hydrationTarget != null ? `${hydrationTarget} ml` : <span className="text-gray-400 italic">Set target</span>}
            <PencilIcon className="w-3.5 h-3.5" />
          </button>
        )}
      </div>

      {/* Daily Intake Bar Chart */}
      {dailyData.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-xl">Daily Hydration</CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={320}>
              <BarChart data={dailyData} margin={{ top: 5, right: 10, left: 0, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#6b7280" opacity={0.15} />
                <XAxis
                  dataKey="date"
                  tickFormatter={formatDate}
                  interval={tickInterval}
                  tick={{ fontSize: 14, fill: '#9ca3af' }}
                  axisLine={{ stroke: '#4b5563' }}
                  tickLine={{ stroke: '#4b5563' }}
                />
                <YAxis
                  tick={{ fontSize: 14, fill: '#9ca3af' }}
                  axisLine={{ stroke: '#4b5563' }}
                  tickLine={{ stroke: '#4b5563' }}
                  width={50}
                  label={{ value: 'ml', angle: -90, position: 'insideLeft', style: { fill: '#9ca3af', fontSize: 14 } }}
                />
                <Tooltip content={<BarTooltip />} />
                <Legend
                  formatter={(value) => value.charAt(0).toUpperCase() + value.slice(1)}
                  wrapperStyle={{ fontSize: 14 }}
                />
                {hydrationTarget && (
                  <ReferenceLine
                    y={hydrationTarget}
                    stroke="#ef4444"
                    strokeDasharray="5 3"
                    label={{ value: `Target ${hydrationTarget} ml`, position: 'insideTopLeft', fill: '#ef4444', fontSize: 14 }}
                  />
                )}
                <Bar dataKey="water" stackId="hyd" fill={BEVERAGE_COLORS.water} name="water" />
                <Bar dataKey="coffee" stackId="hyd" fill={BEVERAGE_COLORS.coffee} name="coffee" />
                <Bar dataKey="tea" stackId="hyd" fill={BEVERAGE_COLORS.tea} name="tea" />
                <Bar dataKey="juice" stackId="hyd" fill={BEVERAGE_COLORS.juice} name="juice" />
                <Bar dataKey="milk" stackId="hyd" fill={BEVERAGE_COLORS.milk} name="milk" />
                <Bar dataKey="soda" stackId="hyd" fill={BEVERAGE_COLORS.soda} name="soda" />
                <Bar dataKey="other" stackId="hyd" fill={BEVERAGE_COLORS.other} name="other" radius={[2, 2, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      )}

      {/* Beverage Breakdown Donut */}
      {donutData.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-xl">Beverage Breakdown</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-col items-center">
              <ResponsiveContainer width="100%" height={260}>
                <PieChart>
                  <Pie
                    data={donutData}
                    cx="50%"
                    cy="50%"
                    innerRadius={60}
                    outerRadius={100}
                    paddingAngle={3}
                    dataKey="value"
                  >
                    {donutData.map((entry, i) => (
                      <Cell key={i} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip content={<DonutTooltip />} />
                </PieChart>
              </ResponsiveContainer>
              <div className="flex flex-wrap gap-4 text-sm mt-2 justify-center">
                {donutData.map(d => (
                  <div key={d.name} className="flex items-center gap-1.5">
                    <span className="w-3 h-3 rounded-full inline-block" style={{ backgroundColor: d.color }} />
                    <span className="text-gray-600 dark:text-gray-400">{d.name} {d.pct}%</span>
                  </div>
                ))}
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Log Table */}
      <div className="space-y-4">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Hydration Log</h3>

        {/* Date Filters */}
        <div className="flex flex-wrap gap-4 items-end">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Start Date</label>
            <Input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">End Date</label>
            <Input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} />
          </div>
          {(startDate || endDate) && (
            <Button variant="outline" size="sm" onClick={() => { setStartDate(''); setEndDate(''); }}>
              Clear Filters
            </Button>
          )}
        </div>

        {logEntries.length === 0 ? (
          <p className="text-gray-600 dark:text-gray-300">
            No hydration entries found for the selected period.
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full border-collapse">
              <thead>
                <tr className="border-b border-gray-200 dark:border-gray-700">
                  <th className="text-left py-3 px-4 font-medium text-gray-900 dark:text-white">Date</th>
                  <th className="text-left py-3 px-4 font-medium text-gray-900 dark:text-white">Beverage</th>
                  <th className="text-left py-3 px-4 font-medium text-gray-900 dark:text-white">Amount (ml)</th>
                  <th className="text-left py-3 px-4 font-medium text-gray-900 dark:text-white">Notes</th>
                  <th className="text-left py-3 px-4 font-medium text-gray-900 dark:text-white">Actions</th>
                </tr>
              </thead>
              <tbody>
                {logEntries.map((entry) => {
                  const data = entry.data || {};
                  const beverage = data.beverage_type || entry.entry_type || 'water';
                  const amount = data.amount_ml || entry.value || '-';

                  return (
                    <tr key={entry.id} className="border-b border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800">
                      <td className="py-3 px-4 text-gray-900 dark:text-white">{new Date(entry.entry_date).toLocaleDateString()}</td>
                      <td className="py-3 px-4 text-gray-900 dark:text-white capitalize">{beverage}</td>
                      <td className="py-3 px-4 text-gray-900 dark:text-white">{amount}</td>
                      <td className="py-3 px-4 text-gray-600 dark:text-gray-300 max-w-xs truncate">{entry.notes || '-'}</td>
                      <td className="py-3 px-4">
                        <div className="flex gap-1">
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handleEditClick(entry)}
                            className="p-2 text-gray-500 hover:text-primary-600"
                            aria-label={`Edit ${beverage} entry`}
                          >
                            <PencilIcon className="w-4 h-4" />
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handleDeleteClick(entry.id)}
                            className="p-2 text-red-600 hover:text-red-700"
                            aria-label={`Delete ${beverage} entry`}
                          >
                            <TrashIcon className="w-4 h-4" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Delete Confirmation Modal */}
      <Modal
        open={deleteDialogOpen}
        onClose={() => { setDeleteDialogOpen(false); setDeletingEntryId(null); }}
        title="Delete Entry"
      >
        <div className="space-y-4">
          <p className="text-gray-700 dark:text-gray-300">
            Are you sure you want to delete this entry? This action cannot be undone.
          </p>
          <div className="flex justify-end space-x-2">
            <Button variant="outline" onClick={() => { setDeleteDialogOpen(false); setDeletingEntryId(null); }}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleDeleteConfirm}>
              Delete
            </Button>
          </div>
        </div>
      </Modal>

      {/* Edit Entry Modal */}
      <Modal
        open={editDialogOpen}
        onClose={() => { setEditDialogOpen(false); setEditingEntry(null); }}
        title="Edit Hydration Entry"
      >
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Date</label>
              <Input
                type="date"
                value={editForm.entry_date || ''}
                onChange={(e) => setEditForm(f => ({ ...f, entry_date: e.target.value }))}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Beverage</label>
              <Input
                value={editForm.beverage_type || ''}
                onChange={(e) => setEditForm(f => ({ ...f, beverage_type: e.target.value }))}
                placeholder="e.g. water, coffee, tea"
              />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Amount (ml)</label>
            <Input
              type="number"
              value={editForm.amount_ml ?? ''}
              onChange={(e) => setEditForm(f => ({ ...f, amount_ml: e.target.value }))}
              min="0"
              step="1"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Notes</label>
            <Input
              value={editForm.notes || ''}
              onChange={(e) => setEditForm(f => ({ ...f, notes: e.target.value }))}
              placeholder="Optional notes"
            />
          </div>
          <div className="flex justify-end space-x-2 pt-2">
            <Button variant="outline" onClick={() => { setEditDialogOpen(false); setEditingEntry(null); }}>
              Cancel
            </Button>
            <Button onClick={handleEditSave}>
              Save Changes
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default HydrationDataView;
